// @noflow
import './styles.css';

import DayLabels from './day-labels';
import moment from 'moment-timezone';
import { momentObj } from 'react-moment-proptypes';
import PropTypes from 'prop-types';
import { range } from 'lodash/fp';
import * as React from 'react';
import { responsiveVars } from '../constants';
import TimelineTicks from './ticks';

const MILLISECONDS_IN_DAY = 86400000;
const MILLISECONDS_IN_HOUR = 3600000;

const { TICKS_PER_DAY } = responsiveVars;

//###### Component Methods ########//

export const getWidthRightStyle = ({ width, right }) => ({
    width: `${width}%`,
    right: `${right}%`
});

export const getLabelWidth = ({ index, positionOffset, tickSpaceWidth, timelineEnd, timezone, zoomLevel }) => {
    const fullDaysWidth = TICKS_PER_DAY * tickSpaceWidth * zoomLevel;

    if (index !== 0) {
        return fullDaysWidth;
    }

    const iteratedDateMS = timelineEnd - MILLISECONDS_IN_DAY * index;
    const iteratedMoment = moment.tz(iteratedDateMS, timezone);
    const iteratedStartOfDay = iteratedMoment.startOf('day').valueOf();
    const timeoffset = iteratedDateMS - iteratedStartOfDay;
    const hoursOffset = Math.floor(timeoffset / MILLISECONDS_IN_HOUR) + 1;

    return hoursOffset * tickSpaceWidth * zoomLevel + positionOffset;
};

export const getLabelDate = ({ index, timelineEnd, timezone }) => {
    const iteratedDateMS = timelineEnd - MILLISECONDS_IN_DAY * index;
    const iteratedMoment = moment.tz(iteratedDateMS, timezone);
    const iteratedStartOfDay = iteratedMoment.startOf('day').valueOf();

    return moment(iteratedStartOfDay).toDate();
};

export const getDayLabelsData = ({ minTicksDisplayed, timelineEnd, timelineStart, timezone, zoomLevel }) => {
    const tickSpaceWidth = 100 / minTicksDisplayed;
    const positionOffset = tickSpaceWidth * 0.5;
    const daysAmount = Math.ceil((timelineEnd - timelineStart) / MILLISECONDS_IN_DAY);

    let iteratedRight = 0;

    const dayLabels = range(0, daysAmount).reduce((all, item, i) => {
        const date = getLabelDate({ index: i, timelineEnd, timezone });
        const width = getLabelWidth({
            timezone,
            index: i,
            positionOffset,
            tickSpaceWidth,
            timelineEnd,
            zoomLevel
        });

        const dateStyle = getWidthRightStyle({
            width,
            right: iteratedRight
        });
        iteratedRight += width;

        return [
            ...all,
            {
                date,
                style: dateStyle
            }
        ];
    }, []);

    return {
        dayLabels: dayLabels
    };
};

export const getNowTickStyle = ({ now, positionOffset, tickSpaceWidth, timelineEndRounded, zoomLevel }) => {
    const nowMS = timelineEndRounded - now.valueOf();
    const widthPerMS = tickSpaceWidth * zoomLevel / MILLISECONDS_IN_HOUR;
    const nowWidth = nowMS * widthPerMS;
    const nowRight = positionOffset + nowWidth;
    return {
        right: `${nowRight}%`
    };
};

export const getTickMoment = ({ timezone, timelineEndRounded, index, zoomLevel }) => {
    const timeMS = timelineEndRounded - MILLISECONDS_IN_HOUR * (index / zoomLevel);
    return moment.tz(timeMS, timezone);
};

export const getTickData = ({ minTicksDisplayed, now, timelineEnd, timelineStart, timezone, zoomLevel }) => {
    const timelineEndRounded = moment(timelineEnd)
        .add(1, 'hour')
        .startOf('hour')
        .valueOf();
    const tickAmount = 1 + (timelineEndRounded - timelineStart) / MILLISECONDS_IN_HOUR * zoomLevel;
    const tickSpaceWidth = 100 / minTicksDisplayed;
    const minTicksPositionOffset = Math.max(minTicksDisplayed - tickAmount, 0) * tickSpaceWidth;
    const positionOffset = tickSpaceWidth * 0.5 + minTicksPositionOffset;

    const ticks = range(0, tickAmount).reduce((all, item, i) => {
        const tickMoment = getTickMoment({
            timezone,
            index: i,
            timelineEndRounded,
            zoomLevel: zoomLevel
        });
        const tickPosition = i * tickSpaceWidth + positionOffset;
        const tickStyle = {
            right: `${tickPosition}%`,
            width: `${i === 0 ? tickSpaceWidth : tickSpaceWidth * 0.5}%`
        };

        return [
            ...all,
            {
                index: i,
                tickMoment,
                tickStyle,
                type: 'tick'
            }
        ];
    }, []);

    if (now.isBetween(timelineStart, timelineEnd, null, '[]')) {
        const nowTickStyle = getNowTickStyle({
            now,
            positionOffset,
            timelineEndRounded,
            tickSpaceWidth,
            zoomLevel
        });
        const nowTick = {
            type: 'now',
            style: nowTickStyle
        };

        return [nowTick, ...ticks];
    }

    return ticks;
};

//###### Component Render ########//

const timelinePropTypes = {
    minTicksDisplayed: PropTypes.number,
    now: momentObj.isRequired,
    timelineEnd: PropTypes.number.isRequired,
    timelineStart: PropTypes.number.isRequired,
    zoomLevel: PropTypes.number.isRequired
};

const Timeline = ({ minTicksDisplayed, now, timelineEnd, timelineStart, zoomLevel }) => {
    const timezone = now.tz();
    const dayLabelsData = getDayLabelsData({
        minTicksDisplayed,
        timelineEnd,
        timelineStart,
        timezone,
        zoomLevel
    });
    const tickData = getTickData({
        minTicksDisplayed,
        now,
        timelineEnd,
        timelineStart,
        timezone,
        zoomLevel
    });
    return (
        <div className="logs-timeline-component__timeline">
            <DayLabels dayLabels={dayLabelsData.dayLabels} />
            <TimelineTicks tickData={tickData} />
        </div>
    );
};

Timeline.propTypes = timelinePropTypes;
export default Timeline;



// WEBPACK FOOTER //
// ./src/components/widgets/logs-timeline/timeline/index.js